import { createFrontendDescendantStateOwnerKey } from "@/app/hooks/frontendDescendantStateOwner";

export type SpecialistLocationGatewayCanonicalScalar =
  | boolean
  | null
  | number
  | string;

/**
 * Canonical, injective tuple encoding used by every SLG operation key.
 *
 * Raw delimiter joins are deliberately unavailable here: JSON arrays retain
 * field boundaries even when an opaque identifier contains punctuation, a NUL
 * byte, or a previous implementation's separator.
 */
export function encodeSpecialistLocationGatewayTuple(
  fields: readonly SpecialistLocationGatewayCanonicalScalar[],
) {
  if (
    fields.some(
      (field) =>
        field !== null &&
        typeof field !== "string" &&
        typeof field !== "boolean" &&
        !(typeof field === "number" && Number.isSafeInteger(field)),
    )
  ) {
    throw new Error(
      "SLG canonical tuples accept only strings, booleans, null, and safe integers.",
    );
  }

  return JSON.stringify(fields);
}

/**
 * Opaque owner key for mutable state derived from an exact SLG handoff.
 * The shared frontend owner primitive keeps this key compatible with all
 * existing booking and commerce descendants.
 */
export function createSpecialistLocationGatewayOwnerKey(input: {
  stage: string;
  fields: readonly SpecialistLocationGatewayCanonicalScalar[];
}) {
  return `specialist-location-gateway-owner-v1:${encodeSpecialistLocationGatewayTuple(
    [input.stage, ...input.fields],
  )}`;
}

/** React-only remount key; never persistence, CAS, or provider authority. */
export function createSpecialistLocationGatewayReactOwnerKey(input: {
  stage: string;
  draftId: string | null;
  customerAccountId: string | null;
  canonicalAuthorityKey: string;
}) {
  return createFrontendDescendantStateOwnerKey({
    surface: "specialist-location-gateway",
    stage: input.stage,
    draftId: input.draftId,
    customerAccountId: input.customerAccountId,
    frozenSource: input.canonicalAuthorityKey,
  });
}
